import React, { useEffect, useMemo, useRef, useState } from "react";
import { DatePicker, Select } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Pencil, Trash2 } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "react-toastify";
import DashboardLayout from "../layout";
import { APP_ROUTES } from "../../constants/approutes";
import EditPrescriptionConsultTab from "./EditPrescriptionConsultTab";
import EditPrescriptionMedicineTab from "./EditPrescriptionMedicineTab";
import EditPrescriptionTestsTab from "./EditPrescriptionTestsTab";
import newLogo from "../../assets/newLogo.png";
import { fetchLifelineQrUrl } from "../../utils/lifelineQr";
import { DEFAULT_LOC, DEFAULT_PINCODE } from "./prescriptionDefaults";

// Prevent duplicate API calls in React 18 StrictMode (dev) by caching in-flight Promises.
const visitDetailsPromiseCache = new Map();
const symptomsPromiseCache = new Map();
const diagnosisPromiseCache = new Map();

/** Same shape whether loaded from cache or fresh fetch; fixes StrictMode + inconsistent `content[0]` usage. */
const normalizeVisitDetailsApiPayload = (raw) => {
  if (raw == null) return null;
  if (Array.isArray(raw.content) && raw.content.length > 0) {
    return raw.content[0];
  }
  if (raw.data && (raw.data.visitDetails != null || Array.isArray(raw.data.drugList))) {
    return raw.data;
  }
  return raw;
};

const joinUrl = (base, path) => {
  const safeBase = String(base || "").trim();
  const safePath = String(path || "").trim();
  if (!safeBase) return safePath;
  if (!safePath) return safeBase;
  return `${safeBase.replace(/\/+$/, "")}/${safePath.replace(/^\/+/, "")}`;
};

/** GCS files may live at bucket root while API returns smart-clinic-docs/filename.jpeg */
const stripSmartClinicDocsPrefix = (path) =>
  String(path || "")
    .trim()
    .replace(/^smart-clinic-docs\//i, "");

const resolveGoogleAssetUrl = (path, baseUrl) => {
  const value = String(path || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) {
    return value.replace(/\/smart-clinic-docs\//i, "/");
  }
  return joinUrl(baseUrl, stripSmartClinicDocsPrefix(value));
};

const getAgeFromDob = (dob) => {
  if (!dob) return "";
  const parsedDob = dayjs(dob);
  if (!parsedDob.isValid()) return "";
  const age = dayjs().diff(parsedDob, "year");
  return age >= 0 ? String(age) : "";
};

const getFullGender = (gender) => {
  const normalizedGender = String(gender || "").trim().toLowerCase();
  if (!normalizedGender) return "";
  if (["m", "male"].includes(normalizedGender)) return "Male";
  if (["f", "female"].includes(normalizedGender)) return "Female";
  if (["o", "other"].includes(normalizedGender)) return "Other";
  return String(gender || "");
};

const getFormattedVisitDateTime = (dateTimeValue) => {
  if (!dateTimeValue) return "";
  const parsedDateTime = dayjs(dateTimeValue);
  if (!parsedDateTime.isValid()) return String(dateTimeValue);
  return parsedDateTime.format("D MMM YYYY, h:mm A");
};

const formatVisitTypeForHTML = (visitType) => {
  const normalizedVisitType = String(visitType || "").trim().toLowerCase();
  if (!normalizedVisitType) return "";
  if (["v", "video", "video consultation", "video-consultation"].includes(normalizedVisitType)) {
    return "Video Consultation";
  }
  if (["i", "inperson", "in person", "in-person", "inperson consultation", "in-person consultation"].includes(normalizedVisitType)) {
    return "In-person Consultation";
  }
  return String(visitType || "");
};

const INSTRUCTION_LABEL_BY_VALUE = {
  "0": "After Meal",
  "1": "Before Meal",
  "2": "None",
};

const formatMedicineInstruction = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const normalized = String(value).trim();
  return INSTRUCTION_LABEL_BY_VALUE[normalized] ?? normalized;
};

const normalizeInstructionToCode = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const normalized = String(value).trim();
  if (INSTRUCTION_LABEL_BY_VALUE[normalized] !== undefined) return normalized;
  const codeFromLabel = Object.entries(INSTRUCTION_LABEL_BY_VALUE).find(
    ([, label]) => label.toLowerCase() === normalized.toLowerCase()
  );
  return codeFromLabel ? codeFromLabel[0] : normalized;
};

// Doctor signature/stamp live in a private bucket; get a signed URL like the rest of the app does.
const fetchSignedFileUrl = async (fileName) => {
  const value = String(fileName || "").trim();
  if (!value || /^(https?:|data:)/i.test(value)) return "";
  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}patient-medical-history/file-url?fileName=${encodeURIComponent(value)}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          tenantId: localStorage.getItem("tenantId") || "",
        },
      }
    );
    if (!res.ok) return "";
    const data = await res.json();
    return typeof data?.signedUrl === "string" ? data.signedUrl : "";
  } catch (e) {
    return "";
  }
};

const convertImageUrlToBase64 = async (url) => {
  if (!url) return "";
  const response = await fetch(url);
  if (!response.ok) return "";
  const blob = await response.blob();
  return await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result || "");
    reader.onerror = () => resolve("");
    reader.readAsDataURL(blob);
  });
};

const generatePrescriptionPDF = async (prescriptionHTML, patientName = "Prescription") => {
  if (!prescriptionHTML) {
    throw new Error("Prescription HTML is empty");
  }

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = prescriptionHTML;
  tempDiv.style.position = "absolute";
  tempDiv.style.left = "-9999px";
  tempDiv.style.top = "0";
  tempDiv.style.width = "210mm";
  tempDiv.style.backgroundColor = "#ffffff";
  tempDiv.style.fontFamily = "Arial, sans-serif";
  // Avoid capturing browser-like scrollbars in the canvas.
  tempDiv.style.overflow = "hidden";
  document.body.appendChild(tempDiv);

  // html2canvas measures the text baseline with a 1x1 <img> appended to <body>;
  // the global `img { display: block }` reset breaks that and shifts all text down.
  const baselineFixStyle = document.createElement("style");
  baselineFixStyle.innerHTML =
    'body > div > img[width="1"][height="1"] { display: inline !important; }';
  document.head.appendChild(baselineFixStyle);

  try {
    if (document.fonts?.ready) await document.fonts.ready;
    const mainCanvas = await html2canvas(tempDiv, {
      scale: 1.2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
    });

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Slice by stable pixel page height so scaling is consistent on every page.
    const scaleMmPerPx = pdfWidth / mainCanvas.width; // mm per canvas pixel (Y)
    const pageHeightPx = Math.max(1, Math.round(pdfHeight / scaleMmPerPx));

    let sourceY = 0;
    let pageIndex = 0;
    while (sourceY < mainCanvas.height) {
      const remainingPx = mainCanvas.height - sourceY;
      const sliceHeightPx = Math.min(pageHeightPx, remainingPx);

      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = mainCanvas.width;
      pageCanvas.height = sliceHeightPx;

      const ctx = pageCanvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(
          mainCanvas,
          0,
          sourceY,
          mainCanvas.width,
          sliceHeightPx,
          0,
          0,
          mainCanvas.width,
          sliceHeightPx
        );
      }

      const pageImgData = pageCanvas.toDataURL("image/png");
      const sliceHeightMm = sliceHeightPx * scaleMmPerPx;

      if (pageIndex > 0) pdf.addPage();
      pdf.addImage(pageImgData, "PNG", 0, 0, pdfWidth, sliceHeightMm);

      sourceY += sliceHeightPx;
      pageIndex += 1;
    }

    const pdfBlob = pdf.output("blob");
    const safeName = String(patientName || "Prescription")
      .trim()
      .replace(/[^a-zA-Z0-9-_ ]+/g, "");
    const filename = `${safeName || "Prescription"}.pdf`;

    return { blob: pdfBlob, filename };
  } finally {
    document.body.removeChild(tempDiv);
    baselineFixStyle.remove();
  }
};

const PRESCRIPTION_BOOKING_URL = "shruti.curebay.com";
const PRESCRIPTION_CUSTOMER_CARE = "+91-8335 000 999";

export const generatePrescriptionHTML = async ({
  payload,
  doctorDetails,
  clinicName = "",
  signatureUrl,
  stampUrl,
  patientAge = "",
  patientGender = "",
  lifelineQrUrl,
  appointmentData,
  vitals,
}) => {
  const lifelineQrSrc = lifelineQrUrl || "";

  let signatureBase64 = "";
  let stampBase64 = "";
  let normalizedSignatureUrl = "";
  let normalizedSmartClinicSignatureUrl = "";
  let normalizedStampUrl = "";
  let normalizedSmartClinicStampUrl = "";
  const IMG_BASEURL = process.env.REACT_APP_IMG_BASEURL || "";
  const normalizeImageUrl = (url) => {
    if (!url || !url.trim()) return "";
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) {
      // Signed URLs must stay untouched or the signature becomes invalid.
      if (url.includes("?")) return url;
      return url.replace(/\/smart-clinic-docs\//i, "/");
    }
    const cleaned = stripSmartClinicDocsPrefix(url);
    return IMG_BASEURL ? joinUrl(IMG_BASEURL, cleaned) : cleaned;
  };
  const normalizeSmartClinicDocUrl = (url) => {
    if (!url || !url.trim()) return "";
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) {
      return url;
    }
    const fileName = stripSmartClinicDocsPrefix(url);
    return IMG_BASEURL ? joinUrl(joinUrl(IMG_BASEURL, "smart-clinic-docs"), fileName) : url;
  };
  const escapeHtmlAttr = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  if (signatureUrl && signatureUrl.trim()) {
    try {
      normalizedSignatureUrl = normalizeImageUrl(signatureUrl);
      normalizedSmartClinicSignatureUrl = normalizeSmartClinicDocUrl(signatureUrl);
      signatureBase64 = await convertImageUrlToBase64(normalizedSignatureUrl);
      if (
        !signatureBase64 &&
        normalizedSmartClinicSignatureUrl &&
        normalizedSmartClinicSignatureUrl !== normalizedSignatureUrl
      ) {
        signatureBase64 = await convertImageUrlToBase64(normalizedSmartClinicSignatureUrl);
      }
      if (!signatureBase64) {
        console.error("❌ Signature base64 conversion failed - empty result");
      }
    } catch (error) {
      console.error("❌ Error converting signature to base64:", error);
      signatureBase64 = "";
    }
  } else {
    console.warn("⚠️ Signature URL is empty or not provided");
  }

  if (stampUrl && stampUrl.trim()) {
    try {
      normalizedStampUrl = normalizeImageUrl(stampUrl);
      normalizedSmartClinicStampUrl = normalizeSmartClinicDocUrl(stampUrl);
      stampBase64 = await convertImageUrlToBase64(normalizedStampUrl);
      if (!stampBase64 && normalizedSmartClinicStampUrl && normalizedSmartClinicStampUrl !== normalizedStampUrl) {
        stampBase64 = await convertImageUrlToBase64(normalizedSmartClinicStampUrl);
      }
      if (!stampBase64) {
        console.error("❌ Stamp base64 conversion failed - empty result");
      }
    } catch (error) {
      console.error("❌ Error converting stamp to base64:", error);
      stampBase64 = "";
    }
  } else {
    console.warn("⚠️ Stamp URL is empty or not provided");
  }

  const esc = escapeHtmlAttr;
  const signatureSrc = signatureBase64 || normalizedSignatureUrl;
  const stampSrc = stampBase64 || normalizedStampUrl;

  const findingsText =
    Array.isArray(payload?.symptomsList) && payload.symptomsList.length > 0
      ? payload.symptomsList
          .map((item) =>
            typeof item === "string" ? item : item?.symptomsName || item?.name || ""
          )
          .filter(Boolean)
          .join(", ")
      : "";

  const doctorDisplayName = doctorDetails?.doctorName || "";
  const doctorQualification =
    doctorDetails?.qualification || doctorDetails?.userQualification || "";
  const doctorRegnNo =
    doctorDetails?.mciverification ||
    doctorDetails?.medicalLicenseNumber ||
    doctorDetails?.userMCIVerification ||
    "";

  const visitDateLabel = getFormattedVisitDateTime(
    appointmentData?.whenAppointment || payload?.visitDate
  );
  const patientLine = [
    payload?.patientName,
    patientGender,
    patientAge ? `${String(patientAge).padStart(2, "0")} Yrs` : "",
  ]
    .filter(Boolean)
    .join(", ");
  const abhaId =
    appointmentData?.abhaNumber ||
    appointmentData?.abhaId ||
    appointmentData?.abhaAddress ||
    "";
  const appointmentRef =
    appointmentData?.appointmentId ||
    appointmentData?.id ||
    payload?.appointmentId ||
    "";

  // ---------- Vitals ----------
  const VITAL_RANGES = {
    pulse: { low: 60, high: 100 },
    temperature: { low: 97, high: 99.5 },
    spo2: { low: 95, high: 100 },
    respirationRate: { low: 12, high: 20 },
    bmi: { low: 18.5, high: 24.9 },
  };
  const vitalStatus = (key, value) => {
    const n = parseFloat(value);
    const range = VITAL_RANGES[key];
    if (!range || Number.isNaN(n)) return "";
    if (n < range.low) return "LOW";
    if (n > range.high) return "HIGH";
    return "NORMAL";
  };
  const bpStatus = (sys, dia) => {
    const s = parseFloat(sys);
    const d = parseFloat(dia);
    if (Number.isNaN(s) || Number.isNaN(d)) return "";
    if (s >= 130 || d >= 85) return "HIGH";
    if (s < 90 || d < 60) return "LOW";
    return "NORMAL";
  };
  const v = vitals || {};
  const bpSys = v.bloodPressureSystolic || v.systolic || "";
  const bpDia = v.bloodPressureDiastolic || v.diastolic || "";
  const bpValue = bpSys && bpDia ? `${bpSys}/${bpDia}` : v.bloodPressure || "";
  const [bpSysFromText, bpDiaFromText] = String(bpValue).split("/");
  const vitalItems = [
    {
      label: "BP",
      value: bpValue,
      unit: "mmHg",
      status: bpStatus(bpSys || bpSysFromText, bpDia || bpDiaFromText),
    },
    { label: "PULSE", value: v.pulse || v.pulseRate, unit: "bpm", key: "pulse" },
    { label: "TEMP", value: v.temperature, unit: "°F", key: "temperature" },
    { label: "SPO<sub>2</sub>", value: v.spo2 || v.oxygenSaturation, unit: "%", key: "spo2" },
    { label: "RR", value: v.respirationRate || v.respiratoryRate, unit: "/min", key: "respirationRate" },
    { label: "BMI", value: v.bmi, unit: "", key: "bmi" },
  ]
    .filter((item) => item.value !== undefined && item.value !== null && item.value !== "")
    .map((item) => ({
      ...item,
      status: item.status ?? vitalStatus(item.key, item.value),
    }));

  const vitalsSection = vitalItems.length
    ? `
      <div class="rx-section">
        <div class="rx-section-heading"><span>VITALS</span><i></i></div>
        <div class="rx-vitals">
          ${vitalItems
            .map(
              (item) => `
            <div class="rx-vital">
              <div class="rx-vital-top">
                <span class="rx-vital-label">${item.label}</span>
                ${item.status ? `<span class="rx-tag rx-tag-${item.status.toLowerCase()}">${item.status}</span>` : ""}
              </div>
              <div class="rx-vital-value">${esc(item.value)} <small>${item.unit}</small></div>
            </div>`
            )
            .join("")}
        </div>
      </div>`
    : "";

  // ---------- Clinical summary ----------
  const formatDiagnosis = (name, code) =>
    [name, code ? `(${code})` : ""].filter(Boolean).join(" ");
  const diagnosisText = [
    formatDiagnosis(payload?.primaryDiagnosis, payload?.primarySymptoms),
    formatDiagnosis(payload?.secondaryDiagnosis, payload?.secondarySymptoms),
  ]
    .filter(Boolean)
    .join(", ");

  const summaryRow = (label, value) => `
    <div class="rx-summary-row">
      <span class="rx-summary-label">${label}</span>
      <span class="rx-summary-value">: ${esc(value || "")}</span>
    </div>`;

  const optionalSummaryRows = [
    ["Findings", findingsText],
    ["Site", payload?.site],
    ["Onset", payload?.onset],
    ["Character", payload?.characteristics],
    ["Radiation", payload?.radiation],
    ["Associated Symptoms", payload?.associatedSymptoms],
    ["Timing", payload?.timing],
    ["Exacerbating Factors", payload?.exacerbatingFactors],
    ["Relieving Factors", payload?.relievingFactors],
    ["Severity", payload?.severity],
    ["Any Treatment Taken", payload?.treatmentTaken],
    ["Comments", payload?.comments],
    ["Frame", payload?.frame],
    ["Coating", payload?.coating],
    ["NV Assessment", payload?.nvAssessment],
    ["DV Assessment", payload?.dvAssessment],
    ["Treatment Option", payload?.treatmentOption],
    ["Surgery Recommendation", payload?.surgeryRecommendation],
  ].filter(([, value]) => value);

  const summaryPairs = [
    ["Chief Complaint", payload?.complaints || payload?.consultReason],
    ["Symptoms", payload?.symptoms],
  ];

  // ---------- Medicines ----------
  const frequencyBlock = (frequency) => {
    const value = String(frequency || "").trim();
    if (!value) return "-";
    const parts = value.split("-");
    const sub =
      parts.length === 4 ? "M-N-E-N" : parts.length === 3 ? "M-A-N" : "";
    return `${esc(value)}${sub ? `<small>${sub}</small>` : ""}`;
  };

  const medicines = Array.isArray(payload?.patientDrugPrescriptionList)
    ? payload.patientDrugPrescriptionList
    : [];
  const medicinesSection = medicines.length
    ? `
      <div class="rx-section">
        <div class="rx-section-heading"><span>MEDICINES PRESCRIBED</span><i></i></div>
        ${medicines
          .map((med, index) => {
            const days = med.duration || med.noOfDays;
            return `
          <div class="rx-med-card">
            <div class="rx-med-index">${index + 1}.</div>
            <div class="rx-med-name">
              <b>${esc(med.drugName || "-")}</b>
              ${med.composition ? `<small>${esc(med.composition)}</small>` : ""}
            </div>
            <div class="rx-med-col"><span>FREQUENCY</span><b>${frequencyBlock(med.frequency)}</b></div>
            <div class="rx-med-col"><span>DURATION</span><b>${days ? `${esc(days)} days` : "-"}</b></div>
            <div class="rx-med-col"><span>INSTRUCTION</span><b>${esc(formatMedicineInstruction(med.instruction ?? med.afterBeforeFood) || "-")}</b></div>
            <div class="rx-med-col"><span>NOTES</span><b class="rx-med-notes">${esc(med.notes || "")}</b></div>
          </div>`;
          })
          .join("")}
      </div>`
    : "";

  // ---------- Diagnostic tests ----------
  const labTests = Array.isArray(payload?.patientLabTestsList)
    ? payload.patientLabTestsList
    : [];
  const labTestsSection = labTests.length
    ? `
      <div class="rx-section">
        <div class="rx-section-heading"><span>DIAGNOSTIC TESTS</span><i></i></div>
        ${labTests
          .map((test, index) => {
            const testType = test.testType || test.labTestType || test.type || "";
            const note = test.instruction || test.notes || test.preparation || "";
            return `
          <div class="rx-test-row">
            <span class="rx-test-index">${index + 1}.</span>
            <span class="rx-test-name">${esc(test.labTestName || "-")}${note ? `<em> · ${esc(note)}</em>` : ""}</span>
            ${testType ? `<span class="rx-test-type">${esc(String(testType).toUpperCase())}</span>` : ""}
          </div>`;
          })
          .join("")}
      </div>`
    : "";

  // ---------- Follow-up & referral ----------
  const followUpDate =
    payload?.followUpVisitDate && payload.followUpVisitDate.includes(" ")
      ? payload.followUpVisitDate.split(" ")[0]
      : payload?.followUpVisitDate || "";
  const followUpDays =
    followUpDate && dayjs(followUpDate).isValid()
      ? dayjs(followUpDate)
          .startOf("day")
          .diff(dayjs(payload?.visitDate || undefined).startOf("day"), "day")
      : null;
  const followUpLabel = !followUpDate
    ? ""
    : followUpDays > 0
    ? `after ${followUpDays} day${followUpDays === 1 ? "" : "s"}`
    : `on ${dayjs(followUpDate).isValid() ? dayjs(followUpDate).format("D MMM YYYY") : followUpDate}`;
  const [rawReferralSpeciality = "", ...referralReasonParts] = String(
    payload?.referral || ""
  ).split(":");
  const referralSpeciality = rawReferralSpeciality.trim();
  const referralReason = referralReasonParts.join(":").trim();
  const referralText = [referralSpeciality, referralReason]
    .filter(Boolean)
    .join(", ");

  const followUpSection =
    followUpLabel || referralText
      ? `
      <div class="rx-follow-row">
        ${followUpLabel ? `<span class="rx-follow"><i class="rx-dot"></i><b>Follow-up ${esc(followUpLabel)}</b>&nbsp;or earlier if symptoms worsen</span>` : ""}
        ${referralText ? `<span class="rx-referral"><i class="rx-dot"></i><b>Doctor Referral</b>&nbsp;${esc(referralText)}</span>` : ""}
      </div>`
      : "";

  // ---------- Medical history ----------
  const historyColumns = [
    { title: "&#9888; ALLERGIES", value: payload?.allergy, allergy: true },
    { title: "PAST MEDICAL HISTORY", value: payload?.pastMedicalHistory },
    { title: "SURGICAL HISTORY", value: payload?.pastSurgicalHistory },
    { title: "CURRENT MEDICATION", value: payload?.currentMedication },
  ];
  const historySection = historyColumns.some((col) => col.value)
    ? `
      <div class="rx-section">
        <div class="rx-section-heading"><span>MEDICAL HISTORY</span><i></i></div>
        <div class="rx-history">
          ${historyColumns
            .map(
              (col) => `
            <div class="rx-history-col${col.allergy ? " rx-history-allergy" : ""}">
              <div class="rx-history-title">${col.title}</div>
              ${String(col.value || "")
                .split(/[,\n]/)
                .map((item) => item.trim())
                .filter(Boolean)
                .map((item) => `<div class="rx-history-item">${esc(item)}</div>`)
                .join("") || `<div class="rx-history-item rx-muted">-</div>`}
            </div>`
            )
            .join("")}
        </div>
      </div>`
    : "";

  const adviceSection = payload?.recommendation
    ? `
      <div class="rx-section">
        <div class="rx-section-heading"><span>ADVICE &amp; INSTRUCTIONS</span><i></i></div>
        <div class="rx-advice">${esc(payload.recommendation)}</div>
      </div>`
    : "";

  const qrImg = (size) =>
    lifelineQrSrc
      ? `<img src="${esc(lifelineQrSrc)}" alt="Lifeline QR" style="width: ${size}px; height: auto; display: block;" onerror="this.style.display='none';" />`
      : "";

  return `
   <html>
      <head>
        <style>
          .rx-page, .rx-page * { box-sizing: border-box; }
          .rx-page { padding: 22px 26px; font-family: "Inter", Arial, Helvetica, sans-serif; color: #1f2937; background: #ffffff; }
          .rx-page p { margin: 0; }
          .rx-muted { color: #9ca3af !important; }

          .rx-header { display: flex; align-items: stretch; padding: 4px 0 14px; border-bottom: 1px solid #e5e7eb; }
          .rx-doc { width: 222px; flex-shrink: 0; padding-right: 18px; border-right: 1px solid #d1d5db; display: flex; flex-direction: column; }
          .rx-doc-name { font-size: 16px; font-weight: 700; color: #111827; margin: 12px 0 6px; }
          .rx-doc-qual { font-size: 10px; color: #4b5563; line-height: 16px; margin-bottom: 6px; }
          .rx-chip { display: block; line-height: 12px; font-size: 9px; color: #6b7280; background: #f9fafb; border: 1px solid #d1d5db; border-radius: 4px; padding: 3px 8px 5px; white-space: nowrap; }
          .rx-chip b { color: #4b5563; font-weight: 700; margin-right: 2px; }
          .rx-doc .rx-chip { margin-top: auto; }
          .rx-patient { flex: 1; display: flex; flex-direction: column; padding: 30px 12px 0 12px; min-width: 0; }
          .rx-patient-row { display: flex; padding-right: 8px; }
          .rx-patient-divider { height: 1px; background: #d1d5db; margin: 10px 8px 12px 0; }
          .rx-field { padding-right: 8px; }
          .rx-patient-row .rx-field:nth-child(1) { width: 35%; }
          .rx-patient-row .rx-field:nth-child(2) { width: 37%; }
          .rx-patient-row .rx-field:nth-child(3) { width: 28%; }
          .rx-patient-row .rx-field.rx-field-wide { width: 72%; }
          .rx-patient-row .rx-field-wide + .rx-field { width: 28%; }
          .rx-field-label { font-size: 9px; color: #4b5563; margin-bottom: 3px; }
          .rx-field-value { font-size: 10.5px; font-weight: 600; color: #111827; white-space: nowrap; }
          .rx-chips { display: flex; gap: 12px; margin-top: auto; padding-top: 12px; }
          .rx-chips .rx-chip { flex: 1; }
          .rx-qr { flex-shrink: 0; padding-left: 16px; border-left: 1px solid #d1d5db; display: flex; align-items: flex-start; padding-top: 6px; }

          .rx-section { margin-top: 16px; }
          .rx-section-heading { display: flex; align-items: center; gap: 12px; margin-bottom: 9px; }
          .rx-section-heading span { font-size: 11px; font-weight: 700; letter-spacing: 0.9px; color: #111827; white-space: nowrap; }
          .rx-section-heading i { flex: 1; height: 1px; background: #d1d5db; }

          .rx-vitals { display: flex; border: 1px solid #e5e7eb; border-radius: 6px; background: #f9fafb; }
          .rx-vital { flex: 1; padding: 8px 10px; border-right: 1px solid #e5e7eb; }
          .rx-vital:last-child { border-right: 0; }
          .rx-vital-top { display: flex; align-items: center; gap: 5px; margin-bottom: 5px; }
          .rx-vital-label { font-size: 8.5px; color: #6b7280; letter-spacing: 0.4px; }
          .rx-vital-value { font-size: 13px; font-weight: 700; color: #111827; }
          .rx-vital-value small { font-size: 8.5px; font-weight: 400; color: #6b7280; }
          .rx-tag { font-size: 7px; font-weight: 700; padding: 1px 4px; border-radius: 2px; letter-spacing: 0.3px; border: 1px solid transparent; }
          .rx-tag-normal { color: #15803d; background: #f0fdf4; border-color: #bbf7d0; }
          .rx-tag-high { color: #c2410c; background: #fff7ed; border-color: #fed7aa; }
          .rx-tag-low { color: #1d4ed8; background: #eff6ff; border-color: #bfdbfe; }

          .rx-summary { display: flex; flex-wrap: wrap; padding: 0 6px; }
          .rx-summary-row { display: flex; width: 50%; padding-right: 14px; font-size: 9.5px; }
          .rx-summary-row.rx-full { width: 100%; padding-right: 0; }
          .rx-summary-label { width: 125px; flex-shrink: 0; font-weight: 700; color: #111827; padding: 6px 0; }
          .rx-summary-value { flex: 1; color: #374151; padding: 6px 0; border-bottom: 1px dashed #d1d5db; }

          .rx-med-card { display: flex; align-items: stretch; border: 1px solid #e5e7eb; border-left: 3px solid #374151; border-radius: 5px; margin-bottom: 6px; overflow: hidden; }
          .rx-med-index { width: 30px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 10.5px; font-weight: 700; color: #111827; background: #f9fafb; border-right: 1px solid #e5e7eb; }
          .rx-med-name { width: 34%; padding: 9px 12px; border-right: 1px solid #e5e7eb; display: flex; flex-direction: column; justify-content: center; }
          .rx-med-name b { display: block; font-size: 10.5px; color: #111827; margin-bottom: 4px; }
          .rx-med-name small { display: block; font-size: 7.5px; color: #6b7280; line-height: 1.4; }
          .rx-med-col { flex: 1; text-align: center; padding: 8px 4px; }
          .rx-med-col span { display: block; font-size: 7.5px; color: #6b7280; letter-spacing: 0.6px; margin-bottom: 3px; }
          .rx-med-col b { display: block; font-size: 10px; line-height: 13px; font-weight: 600; color: #111827; }
          .rx-med-col b small { display: block; font-size: 6.5px; line-height: 9px; font-weight: 400; color: #6b7280; letter-spacing: 1.5px; margin-top: 1px; }
          .rx-med-notes { font-weight: 400 !important; font-size: 9px !important; }

          .rx-test-row { display: flex; align-items: center; padding: 6px 0; font-size: 9.5px; }
          .rx-test-index { width: 28px; color: #374151; }
          .rx-test-name { flex: 1; color: #111827; }
          .rx-test-name em { font-style: normal; color: #6b7280; }
          .rx-test-type { font-size: 7px; color: #6b7280; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 2px; padding: 2px 7px; letter-spacing: 0.5px; }

          .rx-follow-row { margin-top: 12px; padding-top: 10px; border-top: 1px solid #e5e7eb; font-size: 9.5px; line-height: 16px; color: #374151; white-space: nowrap; }
          .rx-follow-row b { color: #111827; font-size: 10.5px; line-height: 16px; }
          .rx-follow, .rx-referral { display: inline-block; vertical-align: top; line-height: 16px; }
          .rx-follow + .rx-referral { margin-left: 14px; padding-left: 14px; border-left: 1px solid #d1d5db; }
          .rx-dot { display: inline-block; width: 5px; height: 5px; border-radius: 50%; background: #374151; margin: 0 7px 1px 0; vertical-align: middle; }

          .rx-history { display: flex; border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden; }
          .rx-history-col { width: 25%; padding: 9px 11px; border-right: 1px solid #e5e7eb; }
          .rx-history-col:last-child { border-right: 0; }
          .rx-history-allergy { background: #fef2f2; }
          .rx-history-title { font-size: 7.5px; font-weight: 700; letter-spacing: 0.4px; color: #1e3a8a; margin-bottom: 7px; }
          .rx-history-allergy .rx-history-title { color: #b91c1c; }
          .rx-history-item { font-size: 9.5px; font-weight: 600; color: #111827; margin-bottom: 6px; }

          .rx-advice { background: #f3f4f6; border-radius: 5px; padding: 14px 18px; font-size: 9px; line-height: 1.7; color: #374151; white-space: pre-line; }

          .rx-footer { display: flex; align-items: center; gap: 12px; margin-top: 22px; padding-top: 14px; border-top: 1px solid #e5e7eb; }
          .rx-footer-qr { flex-shrink: 0; padding: 3px; border: 1px solid #e5e7eb; }
          .rx-disclaimer { flex: 1; border: 1px solid #e5e7eb; border-radius: 5px; padding: 8px 14px; }
          .rx-disclaimer ul { margin: 0; padding: 0; list-style: none; }
          .rx-disclaimer li { display: block; list-style: none; position: relative; padding-left: 11px; font-size: 8px; color: #6b7280; line-height: 13px; }
          .rx-disclaimer li:before { content: ""; position: absolute; left: 0; top: 5px; width: 3px; height: 3px; border-radius: 50%; background: #9ca3af; }
          .rx-sign { width: 170px; flex-shrink: 0; padding-left: 14px; }
          .rx-sign-images { display: flex; align-items: flex-end; gap: 8px; margin-bottom: 6px; }
          .rx-signature { max-width: 110px; max-height: 40px; object-fit: contain; display: block; }
          .rx-stamp { max-width: 50px; max-height: 40px; object-fit: contain; display: block; }
          .rx-sign-name { font-size: 12px; font-weight: 700; color: #111827; }
          .rx-sign-meta { font-size: 8.5px; color: #6b7280; margin-top: 3px; line-height: 1.4; }
          .rx-bottom-bar { display: flex; gap: 10px; margin-top: 10px; }
          .rx-bottom-bar div { flex: 1; padding: 5px 10px; border: 1px solid #d1d5db;padding-bottom:10px; border-radius: 6px; background: #f9fafb; font-size: 10px; color: #374151; white-space: nowrap; overflow: visible; }
          .rx-bottom-bar div + div { padding-left: 24px; }
          .rx-bottom-bar b { color: #111827; font-weight: 700; font-size: 10.5px; }
          .rx-bottom-bar u { color: #111827; font-weight: 700; font-size: 10.5px; }
          .rx-sep { color: #d1d5db; font-size: 9px; margin: 0 3px 0 4px; }
          .rx-sep-wide { margin: 0 10px 0 10px; }
        </style>
      </head>

      <body>
        <div class="rx-page">
          <div class="rx-header">
            <div class="rx-doc">
              <img src="${newLogo}" alt="CureBay Logo" style="height: 44px; width: auto; object-fit: contain; display: block; align-self: flex-start;" onerror="this.style.display='none';" />
              <p class="rx-doc-name">${esc(doctorDisplayName)}</p>
              ${doctorQualification ? `<p class="rx-doc-qual">${esc(doctorQualification)}</p>` : ""}
              ${doctorRegnNo ? `<span class="rx-chip"><b>Regn No :</b> ${esc(doctorRegnNo)}</span>` : ""}
            </div>

            <div class="rx-patient">
              <div class="rx-patient-row">
                <div class="rx-field rx-field-wide">
                  <p class="rx-field-label">Patient Name</p>
                  <p class="rx-field-value">${esc(patientLine)}</p>
                </div>
                <div class="rx-field">
                  <p class="rx-field-label">ABHA ID</p>
                  <p class="rx-field-value">${esc(abhaId || "-")}</p>
                </div>
              </div>
              <div class="rx-patient-divider"></div>
              <div class="rx-patient-row">
                <div class="rx-field">
                  <p class="rx-field-label">Phone Number</p>
                  <p class="rx-field-value">${esc(payload?.patientMobileNo || "-")}</p>
                </div>
                <div class="rx-field">
                  <p class="rx-field-label">Consultation Type</p>
                  <p class="rx-field-value">${esc(formatVisitTypeForHTML(payload?.visitType || "") || "-")}</p>
                </div>
                <div class="rx-field">
                  <p class="rx-field-label">Visit Date</p>
                  <p class="rx-field-value">${esc(visitDateLabel || "-")}</p>
                </div>
              </div>
              <div class="rx-chips">
                <span class="rx-chip"><b>Source :</b> ${esc(clinicName || payload?.clinicId || "-")}</span>
                ${appointmentRef ? `<span class="rx-chip"><b>Appointment ID :</b> ${esc(appointmentRef)}</span>` : ""}
              </div>
            </div>

            <div class="rx-qr">${qrImg(108)}</div>
          </div>

          ${vitalsSection}

          <div class="rx-section">
            <div class="rx-section-heading"><span>CLINICAL SUMMARY</span><i></i></div>
            <div class="rx-summary">
              ${summaryPairs.map(([label, value]) => summaryRow(label, value)).join("")}
              ${optionalSummaryRows.map(([label, value]) => summaryRow(label, value)).join("")}
              <div class="rx-summary-row rx-full">
                <span class="rx-summary-label">Diagnosis</span>
                <span class="rx-summary-value">: ${esc(diagnosisText)}</span>
              </div>
            </div>
          </div>

          ${medicinesSection}
          ${labTestsSection}
          ${followUpSection}
          ${historySection}
          ${adviceSection}

          <div class="rx-footer">
            <div class="rx-footer-qr">${qrImg(56)}</div>
            <div class="rx-disclaimer">
              <ul>
                <li>Prescription denotes provisional advice based on limited teleconsultation inputs; confidential and for the recipient's use only.</li>
                <li>Take medicines strictly as advised; in case of emergency or worsening symptoms, seek immediate in-person care.</li>
                <li>Not valid for medico-legal purposes.</li>
              </ul>
            </div>
            <div class="rx-sign">
              ${signatureSrc || stampSrc ? `
              <div class="rx-sign-images">
                ${signatureSrc ? `
                <img
                  id="signature-image"
                  class="rx-signature"
                  src="${esc(signatureSrc)}"
                  alt="Doctor Signature"
                  crossorigin="anonymous"
                  onerror="if (!this.dataset.smartClinicFallbackTried && '${escapeHtmlAttr(normalizedSmartClinicSignatureUrl)}' && '${escapeHtmlAttr(normalizedSmartClinicSignatureUrl)}' !== this.src) { this.dataset.smartClinicFallbackTried='true'; this.src='${escapeHtmlAttr(normalizedSmartClinicSignatureUrl)}'; } else { this.style.display='none'; }"
                />` : ""}
                ${stampSrc ? `
                <img
                  id="stamp-image"
                  class="rx-stamp"
                  src="${esc(stampSrc)}"
                  alt="Doctor Stamp"
                  crossorigin="anonymous"
                  onerror="if (!this.dataset.smartClinicFallbackTried && '${escapeHtmlAttr(normalizedSmartClinicStampUrl)}' && '${escapeHtmlAttr(normalizedSmartClinicStampUrl)}' !== this.src) { this.dataset.smartClinicFallbackTried='true'; this.src='${escapeHtmlAttr(normalizedSmartClinicStampUrl)}'; } else { this.style.display='none'; }"
                />` : ""}
              </div>` : ""}
              <p class="rx-sign-name">${esc(doctorDisplayName || "Doctor")}</p>
              ${doctorQualification ? `<p class="rx-sign-meta">${esc(doctorQualification)}</p>` : ""}
              ${doctorRegnNo ? `<p class="rx-sign-meta">Regn No: ${esc(doctorRegnNo)}</p>` : ""}
            </div>
          </div>

          <div class="rx-bottom-bar">
            <div>Book a consultation with CureBay's verified doctors-<span class="rx-sep">•</span><u>${PRESCRIPTION_BOOKING_URL}</u></div>
            <div>For any enquiry, dial our Customer Care —<span class="rx-sep rx-sep-wide">•</span><b>${PRESCRIPTION_CUSTOMER_CARE}</b></div>
          </div>
        </div>
      </body>
    </html>
  `;
};

const ReadOnlyField = ({
  label,
  value,
  placeholder = "Type here",
  onChange,
}) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-slate-600">
      {label}
    </label>
    <input
      type="text"
      readOnly={!onChange}
      value={value || ""}
      placeholder={placeholder}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
    />
  </div>
);

const ReadOnlyTextArea = ({
  label,
  value,
  rows = 3,
  placeholder = "Type here",
  onChange,
}) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-slate-600">
      {label}
    </label>
    <textarea
      rows={rows}
      readOnly={!onChange}
      value={value || ""}
      placeholder={placeholder}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      className="w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
    />
  </div>
);

const EditPrescriptionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = process.env.REACT_APP_NEW_DOCTOR_URL;
  const API_BASE = process.env.REACT_APP_API_URL_DOCTOR || "";
  const OCR_API_URL = process.env.REACT_APP_OCR_API_URL;
  const OCR_API_KEY = process.env.REACT_APP_OCR_API_KEY;
  const [activeTab, setActiveTab] = useState("consult");

  const queryAppointmentId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("appointmentId");
  }, [location.search]);

    const appointmentId = location.state?.appointmentId || queryAppointmentId;
    const patientName = location.state?.patientName || "Patient";
    const appointmentData = location.state?.data || {};
console.log(appointmentData, "appointmentData");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [visitDetails, setVisitDetails] = useState(null);
console.log(visitDetails, "visitDetailsfgcvhbj");
  const visitPayload = useMemo(
    () => visitDetails?.data || visitDetails?.result || visitDetails || {},
    [visitDetails]
  );
console.log(visitPayload, "visitPayloaddata");
  // Available symptom options for "Findings" chips (used for ID -> name mapping too)
  const [findingsOptions, setFindingsOptions] = useState([]);
  const [loadingFindings, setLoadingFindings] = useState(false);
  const [findingsError, setFindingsError] = useState("");
  const [selectedFindingIds, setSelectedFindingIds] = useState([]);
  const [isFindingsInitialized, setIsFindingsInitialized] = useState(false);

  const [diagnosisList, setDiagnosisList] = useState([]);
  const [loadingDiagnosis, setLoadingDiagnosis] = useState(false);
  const [diagnosisFetchError, setDiagnosisFetchError] = useState("");
  const [primaryDiagnosisName, setPrimaryDiagnosisName] = useState("");
  const [primaryIcdCode, setPrimaryIcdCode] = useState("");
  const [secondaryDiagnosisName, setSecondaryDiagnosisName] = useState("");
  const [secondaryIcdCode, setSecondaryIcdCode] = useState("");

  const [severityValue, setSeverityValue] = useState(null);
  const [followUpVisitDate, setFollowUpVisitDate] = useState(null);
  useEffect(() => {
    let isActive = true;

    const fetchSymptomsList = async () => {
      try {
        if (!isActive) return;
        setLoadingFindings(true);
        setFindingsError("");

        const token = localStorage.getItem("auth_token");

        if (symptomsPromiseCache.has(API_URL)) {
          const cached = symptomsPromiseCache.get(API_URL);
          const data = await cached;
          if (!isActive) return;
          setFindingsOptions(
            Array.isArray(data?.data)
              ? data.data
              : Array.isArray(data)
              ? data
              : []
          );
          return;
        }

        const promise = fetch(`${API_URL}/Symptoms/list`, {
          method: "GET",
          headers: {
            // Authorization: `Bearer ${token}`,
          },
        }).then(async (res) => {
          if (!res.ok) {
            throw new Error(`Failed to fetch symptoms (${res.status})`);
          }
          return res.json();
        });

        symptomsPromiseCache.set(API_URL, promise);
        const data = await promise;

        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];

        if (!isActive) return;
        setFindingsOptions(list);
      } catch (e) {
        if (!isActive) return;
        setFindingsError("Unable to load findings. Please try again.");
      } finally {
        if (!isActive) return;
        setLoadingFindings(false);
      }
    };

    fetchSymptomsList();
    return () => {
      isActive = false;
    };
  }, [API_URL]);

  const prescription = useMemo(
    () =>
      visitPayload?.prescription ||
      visitPayload?.visitPrescription ||
      visitPayload ||
      {},
    [visitPayload]
  );

  const visitDetailsData = useMemo(() => {
    // API response you shared: { visitDetails: { ... }, drugList: [], labList: [] }
    return visitPayload?.visitDetails || visitPayload || {};
  }, [visitPayload]);

  const [draftVisitDetails, setDraftVisitDetails] = useState({});

  useEffect(() => {
    setDraftVisitDetails(visitDetailsData || {});
  }, [visitDetailsData]);

  const updateDraftField = (key) => (val) =>
    setDraftVisitDetails((prev) => ({
      ...prev,
      [key]: val,
    }));

  useEffect(() => {
    if (!API_URL) return;

    const url = `${API_URL}/Diagnosis/list?termSetId=ICD10&codeLevel=1&status=1`;
    const token = localStorage.getItem("auth_token");

    const cacheKey = url;
    let isActive = true;

    const loadDiagnosisList = async () => {
      try {
        setLoadingDiagnosis(true);
        setDiagnosisFetchError("");

        const cachedPromise = diagnosisPromiseCache.get(cacheKey);
        const promise =
          cachedPromise ||
          fetch(url, {
            method: "GET",
            headers: {
              // Authorization: `Bearer ${token}`,
            },
          }).then(async (res) => {
            if (!res.ok) {
              throw new Error(`Failed to fetch diagnosis list (${res.status})`);
            }
            const data = await res.json();
            return Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
          });

        diagnosisPromiseCache.set(cacheKey, promise);

        const list = await promise;
        if (!isActive) return;
        setDiagnosisList(list);
      } catch (e) {
        if (!isActive) return;
        setDiagnosisFetchError("Unable to load diagnoses. Please try again.");
      } finally {
        if (!isActive) return;
        setLoadingDiagnosis(false);
      }
    };

    loadDiagnosisList();

    return () => {
      isActive = false;
    };
  }, [API_BASE]);

  useEffect(() => {
    // Prefill selected diagnosis + ICD code from visitDetails
    if (!visitDetailsData) return;
    setPrimaryDiagnosisName(visitDetailsData?.primaryDiagnosis || "");
    setPrimaryIcdCode(
      visitDetailsData?.primaryIcd10Code ||
        visitDetailsData?.primarySymptoms ||
        ""
    );
    setSecondaryDiagnosisName(visitDetailsData?.secondaryDiagnosis || "");
    setSecondaryIcdCode(
      visitDetailsData?.secondaryIcd10Code ||
        visitDetailsData?.secondarySymptoms ||
        ""
    );

    setSeverityValue(
      visitDetailsData?.severity !== undefined && visitDetailsData?.severity !== null
        ? String(visitDetailsData?.severity)
        : null
    );

    setFollowUpVisitDate(
      visitDetailsData?.followUpVisitDate
        ? dayjs(visitDetailsData?.followUpVisitDate)
        : null
    );
  }, [visitDetailsData]);


  const findingsLabelById = useMemo(() => {
    const map = new Map();
    (findingsOptions || []).forEach((opt) => {
      // Requirement: use only `symptomsName` as both Select "value" and label.
      const name = String(
        opt?.symptomsName ?? opt?.name ?? opt?.symptomName ?? ""
      );
      if (name) map.set(name, name);
    });
    return map;
  }, [findingsOptions]);

  const findingsSelectOptions = useMemo(() => {
    const optionNames = (findingsOptions || [])
      .map((opt) => {
        const name = opt?.symptomsName ?? opt?.name ?? "";
        if (!name) return null;
        return String(name);
      })
      .filter(Boolean);
    const prefilledSymptoms = String(
      visitDetailsData?.symptomsName ||
        ""
    )
      .split(",")
      .map((s) => String(s).trim())
      .filter(Boolean);

    const mergedNames = Array.from(new Set([...optionNames, ...prefilledSymptoms]));
    return mergedNames.map((name) => ({ value: name, label: name }));
  }, [
    findingsOptions,
    visitDetailsData?.symptomsName,
    draftVisitDetails?.symptomsName,
    draftVisitDetails?.symptoms,
    visitPayload,
  ]);

  const findings = useMemo(() => {
    return (selectedFindingIds || [])
      .map((id) => findingsLabelById.get(String(id)) || String(id || ""))
      .filter(Boolean);
  }, [findingsLabelById, selectedFindingIds]);

  useEffect(() => {
    // Reset findings UI when we change appointment
    setIsFindingsInitialized(false);
    setSelectedFindingIds([]);
  }, [appointmentId]);
  useEffect(() => {
    if (!appointmentId) return;
    if (!visitDetails) return;
    if (isFindingsInitialized) return;

    // Your API returns: visitDetails.symptomsName as comma-separated string.
    const symptomsNameFromVisit =
      visitPayload?.visitDetails?.symptomsName ||
      visitDetailsData?.symptomsName ||
      draftVisitDetails?.symptomsName ||
      draftVisitDetails?.symptoms ||
      "";

    const rawFindings =
      symptomsNameFromVisit ||
      [];

    let names = [];

    if (typeof rawFindings === "string") {
      names = rawFindings
        .split(",")
        .map((s) => String(s).trim())
        .filter(Boolean);
    } else if (Array.isArray(rawFindings)) {
      names = rawFindings
        .map((item) => {
          if (!item) return null;
          if (typeof item === "string") return item.trim();
          return item?.symptomsName ||  null;
        })
        .filter(Boolean);
    }

    const normalizedOptionNameMap = new Map();
    (findingsOptions || []).forEach((opt) => {
      const optionName = String(opt?.symptomsName ?? opt?.name ?? "").trim();
      if (!optionName) return;
      normalizedOptionNameMap.set(optionName.toLowerCase(), optionName);
    });

    const resolvedFindings = Array.from(
      new Set(
        names
          .map((x) => String(x || "").trim())
          .filter(Boolean)
          .map((name) => normalizedOptionNameMap.get(name.toLowerCase()) || name)
      )
    );

    setSelectedFindingIds(resolvedFindings);
    setIsFindingsInitialized(true);
  }, [
    appointmentId,
    findingsOptions,
    isFindingsInitialized,
    draftVisitDetails?.symptoms,
    draftVisitDetails?.symptomsName,
    visitDetailsData?.symptomsName,
    visitPayload,
  ]);

  const [medicineItems, setMedicineItems] = useState([]);
  const [labTestItems, setLabTestItems] = useState([]);
  const [addTestName, setAddTestName] = useState("");
  const [isUpdatingPrescription, setIsUpdatingPrescription] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState("");

  // OCR state
  const ocrFileInputRef = useRef(null);
  const [loadingOcr, setLoadingOcr] = useState(false);
  const [ocrError, setOcrError] = useState("");
  const [ocrPreviewURL, setOcrPreviewURL] = useState("");
  const [ocrUploadedFile, setOcrUploadedFile] = useState(null);
  const [ocrPreviewModalOpen, setOcrPreviewModalOpen] = useState(false);

  useEffect(() => {
    const list = Array.isArray(visitPayload?.drugList)
      ? visitPayload.drugList
      : [];
    setMedicineItems(list);
  }, [visitPayload?.drugList]);

  useEffect(() => {
    const list = Array.isArray(visitPayload?.labList)
      ? visitPayload.labList
      : [];
    setLabTestItems(list);
  }, [visitPayload?.labList]);

  const fetchOcrMedicineDetails = async (drugName) => {
    try {
      const pincode =
        appointmentData?.pincode ||
        DEFAULT_PINCODE;
      const loc = String(
        appointmentData?.state ||
          DEFAULT_LOC
      ).toLowerCase();
      const res = await fetch(
        `${process.env.REACT_APP_API_ES_V3_URL}medicine/pincode?pageNo=1&pageSize=20&pincode=${encodeURIComponent(
          pincode
        )}&loc=${encodeURIComponent(loc)}&freeTextSearch=${encodeURIComponent(drugName)}`
      );
      const result = await res.json();
      if (Array.isArray(result) && result.length > 0) {
        const m = result[0];
        return {
          composition: m.composition || "",
          drugCode: m.id || "",
          medicineRate: m.medicineRate || "",
          medicineTypeOfSell: m.medicineTypeOfSell || "",
          drugCategory: m.drugCategory || "",
          prescriptionRequired: m.prescriptionRequired || "",
        };
      }
    } catch (err) {
      console.error(`OCR medicine details fetch failed for ${drugName}:`, err);
    }
    return { composition: "", drugCode: "", medicineRate: "", medicineTypeOfSell: "", drugCategory: "", prescriptionRequired: "" };
  };

  const fetchOcrLabTestDetails = async (labTestName) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_DIAGNOSISURL}partner-labs?labCategory=LAB&freeTextSearch=${encodeURIComponent(labTestName)}`
      );
      const data = await res.json();
      if (Array.isArray(data)) {
        const match = data.find((t) =>
          t?.displayName?.toLowerCase().includes(labTestName.toLowerCase())
        );
        return {
          labTestName: match?.displayName || labTestName,
          labTestId: match?.Curebaytestcode || "",
          amount: match?.listPrice || "",
          labType: match?.labType || "",
        };
      }
    } catch (err) {
      console.error(`OCR lab test fetch failed for ${labTestName}:`, err);
    }
    return { labTestName, labTestId: "", amount: "", labType: "" };
  };

  const handlePrescriptionUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setOcrUploadedFile(file);
    setOcrPreviewURL(URL.createObjectURL(file));
    setOcrError("");
    const formData = new FormData();
    formData.append("file", file);
    try {
      setLoadingOcr(true);
      const response = await fetch(`${OCR_API_URL}medicine_prescription/process?source_channel=DOCTOR`, {
        method: "POST",
        headers: { "X-API-Key": OCR_API_KEY },
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`OCR request failed (${response.status})`);
      }
      const data = await response.json();

      // Map OCR response to UI state — data mapping errors are silently caught
      try {
        const appointmentDate = appointmentData?.whenAppointment
          ? new Date(appointmentData.whenAppointment)
          : new Date();
        const addDays = (days) => {
          const d = new Date(appointmentDate);
          d.setDate(d.getDate() + days);
          return d.toISOString().split("T")[0];
        };

        // Medicines
        const enrichedMedicines = await Promise.all(
          (data?.medicine_details || []).map(async (item, idx) => {
            const details = await fetchOcrMedicineDetails(item?.name || "");
            const instructionRaw = (item?.instructions || "").toLowerCase();
            const instruction = instructionRaw.includes("after")
              ? "0"
              : instructionRaw.includes("before")
              ? "1"
              : "2";
            return {
              id: `ocr-med-${idx}-${item?.name || ""}`,
              drugName: item?.name || "",
              medicineName: item?.name || "",
              composition: details.composition,
              drugCode: details.drugCode,
              drugCategory: details.drugCategory,
              medicineRate: details.medicineRate,
              medicineTypeOfSell: details.medicineTypeOfSell,
              prescriptionRequired: details.prescriptionRequired,
              dosage: "",
              frequency: item?.dosage || "",
              noOfDays: item?.duration != null ? String(item.duration) : "",
              duration: item?.duration != null ? String(item.duration) : "",
              quantity: item?.quantity != null ? String(item.quantity) : "",
              instruction,
              notes: item?.notes || "",
              drugMType: "Medicine",
              status: 1,
            };
          })
        );

        // Lab tests
        const labTestList = await Promise.all(
          (data?.lab_test_details?.tests || []).map(async (test, idx) => {
            const details = await fetchOcrLabTestDetails(test);
            return {
              id: `custom-test-${Date.now()}-${idx}`,
              source: "ocr",
              labTestName: details.labTestName,
              labTestId: details.labTestId,
              amount: details.amount,
              labType: details.labType,
              status: 1,
            };
          })
        );

        // Follow-up date
        let followUpDate = null;
        if (data?.schedule_followup) {
          if (data.schedule_followup.after_1_week) {
            followUpDate = addDays(7);
          } else if (data.schedule_followup.after_15_days) {
            followUpDate = addDays(15);
          } else if (data.schedule_followup.after_1_month) {
            followUpDate = addDays(30);
          }
        }

        setMedicineItems(enrichedMedicines);
        setLabTestItems(labTestList);
        if (data?.recommendation) {
          updateDraftField("recommendation")(data.recommendation);
        }
        if (data?.symptoms) {
          updateDraftField("symptoms")(data.symptoms);
        }
        if (followUpDate) {
          setFollowUpVisitDate(dayjs(followUpDate));
        }
      } catch (mappingErr) {
        // silently swallow mapping errors — UI state may be partially populated
        console.error("OCR data mapping error:", mappingErr);
      }

      setOcrPreviewModalOpen(true);
    } catch (err) {
      setOcrError("Unable to process file. Please try again.");
    } finally {
      setLoadingOcr(false);
      // reset file input so the same file can be re-uploaded
      if (ocrFileInputRef.current) ocrFileInputRef.current.value = "";
    }
  };

  const handleUpdatePrescription = async () => {
    try {
      setIsUpdatingPrescription(true);
      setUpdateError("");
      setUpdateSuccess("");
      const currentUserId =
        visitDetailsData?.userId ||
        visitDetailsData?.modifiedBy ||
        visitDetailsData?.createdBy ||
        "";
      const currentPatientId =
        visitDetailsData?.patientId || visitPayload?.patientId || "";
      const normalizedMedicineItems = (Array.isArray(medicineItems) ? medicineItems : []).map(
        (med) => {
          const instructionCode = normalizeInstructionToCode(
            med?.instruction ?? med?.afterBeforeFood
          );
          return {
          composition: med?.composition || "",
          afterBeforeFood: instructionCode,
          type: med?.type || med?.medicineType || "",
          createdBy: currentUserId,
          dosage: med?.dosage || "",
          drugName: med?.medicineName || med?.drugName || "",
          drugName1: "",
          duration: med?.noOfDays
            ? Number(med.noOfDays)
            : med?.duration
              ? Number(med.duration)
              : 0,
          drugCategory: med?.type || med?.drugCategory || "",
          form: "",
          frequency: med?.frequency || "",
          fromDate: "",
          fromDateTime: "",
          givenDate: "",
          instruction: instructionCode,
          medicineType: med?.type || med?.drugCategory || "",
          modifiedBy: currentUserId,
          patientId: currentPatientId,
          pharmacyStockId: "",
          prescriptionDoc: "",
          prescriptionId: "1",
          drugMType: med?.drugMType || "Medicine",
          quantity: med?.quantity ? String(med.quantity) : "",
          specialAuthority: "",
          status: 1,
          toDate: "",
          toDateTime: "",
          userId: currentUserId,
          itemCode: med?.drugCode || "",
          drugCode: med?.drugCode || "",
          drugType: med?.drugType || "M",
          rate: med?.rate || "",
          typeOfSell: med?.typeOfSell || "",
          notes: med?.notes || "",
          preferredMedicine: med?.preferredMedicine || "",
          isSubstitute: med?.isSubstitute || "",
        };
        }
      );
      const normalizedLabTestItems = (Array.isArray(labTestItems) ? labTestItems : []).map(
        (test) => ({
          createdBy: currentUserId,
          labTestId: test?.labTestId || test?.patientLabTestId || test?.id || "",
          labTestName:
            test?.labTestName ||
            test?.labName ||
            test?.name ||
            test?.displayName ||
            "",
          modifiedBy: currentUserId,
          prescriptionId: "1",
          parentLabTestId: "",
          patientId: currentPatientId,
          status: 1,
          userId: currentUserId,
        })
      );

      const payload = {
        id: visitDetailsData?.id || "",
        edit: true,
        appointmentId: visitDetailsData?.appointmentId || "",
        isFollowUp: visitDetailsData?.isFollowUp ?? "",
        complaints: draftVisitDetails?.complaints || "",
        consultReason: draftVisitDetails?.consultReason || "",
        consultationType: visitDetailsData?.visitType || "",
        createdBy: visitDetailsData?.createdBy || "",
        userId:
          visitDetailsData?.userId || "",
        modifiedBy: visitDetailsData?.modifiedBy || "",
        patientDocument: draftVisitDetails?.patientDocument || "",
        followUpVisitDate: followUpVisitDate
          ? followUpVisitDate.format("YYYY-MM-DD")
          : draftVisitDetails?.followUpVisitDate || "",
        primaryDiagnosis: primaryDiagnosisName || draftVisitDetails?.primaryDiagnosis || "",
        primarySymptoms: primaryIcdCode || draftVisitDetails?.primarySymptoms || "",
        recommendation: draftVisitDetails?.recommendation || "",
        roleCode: visitDetailsData?.roleCode || "",
        secondaryDiagnosis:
          secondaryDiagnosisName || draftVisitDetails?.secondaryDiagnosis || "",
        secondarySymptoms: secondaryIcdCode || draftVisitDetails?.secondarySymptoms || "",
        serviceProvided: "Consultation",
        patientId: visitDetailsData?.patientId || visitPayload?.patientId || "",
        symptoms:
          draftVisitDetails?.symptoms ,
        onset: draftVisitDetails?.onset || "",
        characteristics: draftVisitDetails?.characteristics || "",
        aggravatingFactors: draftVisitDetails?.aggravatingFactors || "",
        relievingFactors: draftVisitDetails?.relievingFactors || "",
        treatmentTaken: draftVisitDetails?.treatmentTaken || "",
        adverseEffect: draftVisitDetails?.adverseEffect || "",
        severity: severityValue || draftVisitDetails?.severity || "",
        frame: draftVisitDetails?.frame || "",
        coating: draftVisitDetails?.coating || "",
        nvAssessment: draftVisitDetails?.nvAssessment || "",
        dvAssessment: draftVisitDetails?.dvAssessment || "",
        treatmentOption: draftVisitDetails?.treatmentOption || "",
        surgeryRecommendation: draftVisitDetails?.surgeryRecommendation || "",
        visitDate: (() => {
            const raw = visitDetailsData?.visitDate || appointmentData?.whenAppointment;
            if (raw) {
              const base = raw.replace("T", " ").substring(0, 19);
              return base.length === 10 ? `${base} 00:00:00` : base;
            }
            const now = new Date();
            const pad = (n) => String(n).padStart(2, "0");
            return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
          })(),
        visitSummary: draftVisitDetails?.visitSummary || "",
        visitType: visitDetailsData?.visitType || "",
        site: draftVisitDetails?.site || "",
        radiation: draftVisitDetails?.radiation || "",
        associatedSymptoms: draftVisitDetails?.associatedSymptoms || "",
        timing: draftVisitDetails?.timing || "",
        comments: draftVisitDetails?.comments || "",
        exacerbatingFactors: draftVisitDetails?.exacerbatingFactors || "",
        patientLabTestsList: normalizedLabTestItems,
        linkType: "e-prescription",
        // symptomsList: Array.isArray(selectedFindingIds)
        //   ? selectedFindingIds
        //       .map((item) => {
        //         if (typeof item === "string") {
        //           return { symptomsName: item };
        //         }
        //         const name =
        //           item?.symptomsName || item?.name || item?.symptomName || "";
        //         return name ? { symptomsName: name } : null;
        //       })
        //       .filter(Boolean)
        //   : [],
        patientDrugPrescriptionList: normalizedMedicineItems,
        patientName: `${appointmentData?.firstName || ""} ${appointmentData?.lastName || ""}`.trim(),
        patientMobileNo: appointmentData?.mobile || "",
        url: visitDetailsData?.url || "",
        eyeReadingMain: visitDetailsData?.eyeReadingMain || {},
        symptomsName: draftVisitDetails?.symptomsName || "",
  referral: draftVisitDetails?.referral || "",
  allergy: draftVisitDetails?.allergy || "",
  pastMedicalHistory: draftVisitDetails?.pastMedicalHistory || "",
  pastSurgicalHistory: draftVisitDetails?.pastSurgicalHistory || ""
      };
      const lifelineQrUrl = await fetchLifelineQrUrl();
      const googleAssetBaseUrl = process.env.REACT_APP_API_GOOGLE_URL || "";
      const signaturePath = visitDetails?.userDetails?.signature || "";
      const stampPath = visitDetails?.userDetails?.stamp || "";
      const [signedSignatureUrl, signedStampUrl] = await Promise.all([
        fetchSignedFileUrl(signaturePath),
        fetchSignedFileUrl(stampPath),
      ]);
      console.log("payloaduygui", visitDetails?.userDetails);
      const prescriptionHTML = await generatePrescriptionHTML({
        payload,
        doctorDetails:visitDetails?.userDetails,
        clinicName:appointmentData?.sourceChannel || "eClinic",
        signatureUrl:
          signedSignatureUrl ||
          resolveGoogleAssetUrl(signaturePath, googleAssetBaseUrl),
        stampUrl:
          signedStampUrl || resolveGoogleAssetUrl(stampPath, googleAssetBaseUrl),
        patientAge: getAgeFromDob(appointmentData?.dob),
        patientGender: getFullGender(appointmentData?.gender),
        lifelineQrUrl,
        appointmentData:appointmentData,
        vitals:
          visitDetailsData?.vitals ||
          visitDetailsData?.patientVitals ||
          appointmentData?.vitals,
      });
      // const previewWindow = window.open("", "_blank");
      // if (!previewWindow) {
      //   throw new Error("Popup blocked");
      // }
      // previewWindow.document.open();
      // previewWindow.document.write(prescriptionHTML);
      // previewWindow.document.close();
      // setTimeout(() => {
      //   previewWindow.focus();
      //   previewWindow.print();
      // }, 400);

      // setUpdateSuccess("Prescription preview opened. Save as PDF from print dialog.");

      let pdfFile = null;
      let pdfBase64 = null;
      try {
        const pdfResult = await generatePrescriptionPDF(
          prescriptionHTML,
          payload?.patientName || "Prescription",
        );
        pdfFile = pdfResult;

        if (pdfFile && pdfFile.blob) {
          pdfBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64String = reader.result.split(",")[1] || reader.result;
              resolve(base64String);
            };
            reader.onerror = reject;
            reader.readAsDataURL(pdfFile.blob);
          });
        }
      } catch (pdfError) {
        // console.error("PDF generation error:", pdfError);
      }

      const formData = new FormData();
      formData.append("visit", JSON.stringify(payload));
      if (pdfFile && pdfFile.blob) {
        const fileData = pdfFile.blob;
        const fileSizeInBytes = fileData.size;
        const fileSizeInKB = (fileData.size / 1024).toFixed(2);
        const fileSizeInMB = (fileData.size / (1024 * 1024)).toFixed(2);
        // console.log(`PDF File size: ${fileSizeInBytes} bytes (${fileSizeInKB} KB / ${fileSizeInMB} MB)`);
        formData.append("file", fileData, pdfFile.filename);
      }

      // if (pdfFile && pdfFile.blob) {
      //   const fileData = pdfFile.blob;
      //   const fileSizeInBytes = fileData.size;
      //   const fileSizeInKB = (fileData.size / 1024).toFixed(2);
      //   const fileSizeInMB = (fileData.size / (1024 * 1024)).toFixed(2);
      //   // console.log(`PDF File size: ${fileSizeInBytes} bytes (${fileSizeInKB} KB / ${fileSizeInMB} MB)`);
      //   formData.append("file", fileData, pdfFile.filename);
      // }
      // if (pdfBase64) {
      //   formData.append("prescriptionDocData", pdfBase64);
      // }
      console.log("visit payload object:", payload);
      console.log("pdfFile ready:", !!pdfFile, pdfFile?.filename || "");
      for (const [key, value] of formData.entries()) {
        if (value instanceof File || value instanceof Blob) {
          console.log("formData entry:", key, {
            name: value.name || pdfFile?.filename || "",
            type: value.type || "application/octet-stream",
            size: value.size,
          });
        } else {
          const printable =
            typeof value === "string" && value.length > 300
              ? `${value.slice(0, 300)}...`
              : value;
          console.log("formData entry:", key, printable);
        }
      }
      const endpoint = joinUrl(API_URL, "api/visits");
      console.log("POST endpoint:", endpoint);
      const res = await fetch(endpoint, {
        method: "POST",
        body: formData,
      });
      if (res.status === 201) {
        const successMessage = "Prescription updated successfully.";
        setUpdateSuccess(successMessage);
        toast.success(successMessage);
        navigate(-1);
      } else {
        const errorMessage = `Unable to update prescription (${res.status}). Please try again.`;
        setUpdateError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (submitError) {
      const errorMessage = "Unable to update prescription. Please try again.";
      setUpdateError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsUpdatingPrescription(false);
    }
  };

  const getDiagnosisName = (d) =>
    d?.termName ??
    d?.name ??
    d?.diagnosisName ??
    d?.description ??
    d?.displayName ??
    "";

  const getDiagnosisCode = (d) =>
    d?.termCode ??
    d?.code ??
    d?.icd10Code ??
    d?.icdCode ??
    d?.diagnosisCode ??
    d?.icd10 ??
    "";

  const diagnosisCodeToName = useMemo(() => {
    const map = new Map();
    (diagnosisList || []).forEach((d) => {
      const code = String(getDiagnosisCode(d) || "");
      const name = String(getDiagnosisName(d) || "");
      if (code && name) map.set(code, name);
    });
    return map;
  }, [diagnosisList]);

  const diagnosisNameToCode = useMemo(() => {
    const map = new Map();
    (diagnosisList || []).forEach((d) => {
      const code = String(getDiagnosisCode(d) || "");
      const name = String(getDiagnosisName(d) || "");
      if (name && code) map.set(name, code);
    });
    return map;
  }, [diagnosisList]);

  const diagnosisNameOptions = useMemo(() => {
    const set = new Set();
    const opts = [];
    (diagnosisList || []).forEach((d) => {
      const name = String(getDiagnosisName(d) || "");
      if (!name || set.has(name)) return;
      set.add(name);
      opts.push({ value: name, label: name });
    });
    return opts;
  }, [diagnosisList]);

  const diagnosisCodeOptions = useMemo(() => {
    const set = new Set();
    const opts = [];
    (diagnosisList || []).forEach((d) => {
      const code = String(getDiagnosisCode(d) || "");
      if (!code || set.has(code)) return;
      set.add(code);
      opts.push({ value: code, label: code });
    });
    return opts;
  }, [diagnosisList]);

  const diagnosisNameOptionsWithSelected = useMemo(() => {
    const opts = [...diagnosisNameOptions];
    const ensure = (name) => {
      if (!name) return;
      if (!opts.some((o) => o.value === name)) {
        opts.push({ value: name, label: name });
      }
    };
    ensure(primaryDiagnosisName);
    ensure(secondaryDiagnosisName);
    return opts;
  }, [diagnosisNameOptions, primaryDiagnosisName, secondaryDiagnosisName]);

  const diagnosisCodeOptionsWithSelected = useMemo(() => {
    const opts = [...diagnosisCodeOptions];
    const ensure = (code) => {
      if (!code) return;
      if (!opts.some((o) => o.value === code)) {
        opts.push({ value: code, label: code });
      }
    };
    ensure(primaryIcdCode);
    ensure(secondaryIcdCode);
    return opts;
  }, [diagnosisCodeOptions, primaryIcdCode, secondaryIcdCode]);

  useEffect(() => {
    // if (!appointmentId) return;
console.log("appointmentId", appointmentId);
    const fetchVisitDetails = async () => {
      // if (!appointmentId) return;
      const baseUrl = String(API_URL || "").replace(/\/$/, "");
      const cacheKey = `${baseUrl}|${appointmentId}`;

      try {
        setLoading(true);
        setError("");
        setVisitDetails(null);

        if (visitDetailsPromiseCache.has(cacheKey)) {
          const cached = visitDetailsPromiseCache.get(cacheKey);
          const data = await cached;
          if (!isActive) return;
          setVisitDetails(normalizeVisitDetailsApiPayload(data));
          return;
        }

        const promise = fetch(
          `${baseUrl}/api/visit/details?appointmentId=${appointmentId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        ).then(async (response) => {
          if (!response.ok) {
            throw new Error(`Failed with status ${response.status}`);
          }
          return response.json();
        });

        visitDetailsPromiseCache.set(cacheKey, promise);
        const data = await promise;
        if (!isActive) return;
        setVisitDetails(normalizeVisitDetailsApiPayload(data));
      } catch (fetchError) {
        visitDetailsPromiseCache.delete(cacheKey);
        if (!isActive) return;
        setError("Unable to fetch visit details.");
      } finally {
        // Keep cache only for in-flight dedupe; clear it after completion
        // so reopening the same appointment triggers a fresh API call.
        visitDetailsPromiseCache.delete(cacheKey);
        if (!isActive) return;
        setLoading(false);
      }
    };

     let isActive = true;
    fetchVisitDetails();

    return () => {
      isActive = false;
    };
  }, [API_URL, appointmentId]);

  return (
    <DashboardLayout>
      <div className="mt-6 px-4">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800">
              Edit Prescription
            </h1>
            <p className="text-sm text-slate-500">
              {patientName} {appointmentId ? `- Appointment #${appointmentId}` : ""}
            </p>
          </div>
         
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          {!appointmentId ? (
            <p className="text-sm text-red-600">Appointment ID is missing.</p>
          ) : null}

          {loading ? (
            <p className="text-sm text-slate-600">Loading visit details...</p>
          ) : null}

          {!loading && error ? (
            <p className="text-sm font-medium text-red-600">{error}</p>
          ) : null}

          {!loading && !error && appointmentId ? (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveTab("consult")}
                  className={`rounded-full px-4 py-1.5 text-sm ${
                    activeTab === "consult"
                      ? "bg-[#0f4168] text-white"
                      : "border border-slate-200 bg-white text-slate-700"
                  }`}
                >
                  Consult
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("medicine")}
                  className={`rounded-full px-4 py-1.5 text-sm ${
                    activeTab === "medicine"
                      ? "bg-[#0f4168] text-white"
                      : "border border-slate-200 bg-white text-slate-700"
                  }`}
                >
                  Medicine
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("tests")}
                  className={`rounded-full px-4 py-1.5 text-sm ${
                    activeTab === "tests"
                      ? "bg-[#0f4168] text-white"
                      : "border border-slate-200 bg-white text-slate-700"
                  }`}
                >
                  Tests
                </button>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => ocrFileInputRef.current?.click()}
                    disabled={loadingOcr}
                    className="rounded-full border border-[#0f4168] px-4 py-1.5 text-sm text-[#0f4168] hover:bg-[#0f4168] hover:text-white disabled:opacity-60"
                  >
                    {loadingOcr ? "Scanning..." : "Scan Prescription"}
                  </button>
                  <input
                    ref={ocrFileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handlePrescriptionUpload}
                    className="hidden"
                  />
                </div>
              </div>
              {ocrError ? (
                <p className="mb-3 text-sm font-medium text-red-600">{ocrError}</p>
              ) : null}

              {activeTab === "consult" ? (
                <>
                  <EditPrescriptionConsultTab
                    findingsError={findingsError}
                    loadingFindings={loadingFindings}
                    selectedFindingIds={selectedFindingIds}
                    setSelectedFindingIds={setSelectedFindingIds}
                    findingsSelectOptions={findingsSelectOptions}
                    primaryDiagnosisName={primaryDiagnosisName}
                    setPrimaryDiagnosisName={setPrimaryDiagnosisName}
                    primaryIcdCode={primaryIcdCode}
                    setPrimaryIcdCode={setPrimaryIcdCode}
                    secondaryDiagnosisName={secondaryDiagnosisName}
                    setSecondaryDiagnosisName={setSecondaryDiagnosisName}
                    secondaryIcdCode={secondaryIcdCode}
                    setSecondaryIcdCode={setSecondaryIcdCode}
                    diagnosisNameOptionsWithSelected={
                      diagnosisNameOptionsWithSelected
                    }
                    diagnosisCodeOptionsWithSelected={
                      diagnosisCodeOptionsWithSelected
                    }
                    loadingDiagnosis={loadingDiagnosis}
                    diagnosisNameToCode={diagnosisNameToCode}
                    diagnosisCodeToName={diagnosisCodeToName}
                    draftVisitDetails={draftVisitDetails}
                    updateDraftField={updateDraftField}
                    findings={findings}
                    severityValue={severityValue}
                    setSeverityValue={setSeverityValue}
                    followUpVisitDate={followUpVisitDate}
                    setFollowUpVisitDate={setFollowUpVisitDate}
                  />
                  <div className="hidden">
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-slate-700">Findings</h3>
                    {findingsError ? (
                      <p className="mb-2 text-sm font-medium text-red-600">
                        {findingsError}
                      </p>
                    ) : null}
                    <Select
                      mode="multiple"
                      allowClear
                      loading={loadingFindings}
                      placeholder="Select findings"
                      value={selectedFindingIds}
                      onChange={(values) => setSelectedFindingIds(values)}
                      options={findingsSelectOptions}
                      className="w-full"
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div className="rounded-md border border-slate-200 p-3">
                    <h3 className="mb-3 text-sm font-semibold text-slate-700">
                      Provisional Diagnosis
                    </h3>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-600">
                          Primary
                        </label>
                        <Select
                          showSearch
                          allowClear
                          placeholder="Select diagnosis"
                          value={primaryDiagnosisName || undefined}
                          onChange={(val) => {
                            const name = val || "";
                            setPrimaryDiagnosisName(name);
                            setPrimaryIcdCode(diagnosisNameToCode.get(name) || "");
                          }}
                          options={diagnosisNameOptionsWithSelected}
                          loading={loadingDiagnosis}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-600">
                          ICD-10 code
                        </label>
                        <Select
                          showSearch
                          allowClear
                          placeholder="Select"
                          value={primaryIcdCode || undefined}
                          onChange={(val) => {
                            const code = val || "";
                            setPrimaryIcdCode(code);
                            setPrimaryDiagnosisName(diagnosisCodeToName.get(code) || "");
                          }}
                          options={diagnosisCodeOptionsWithSelected}
                          loading={loadingDiagnosis}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-600">
                          Secondary
                        </label>
                        <Select
                          showSearch
                          allowClear
                          placeholder="Select diagnosis"
                          value={secondaryDiagnosisName || undefined}
                          onChange={(val) => {
                            const name = val || "";
                            setSecondaryDiagnosisName(name);
                            setSecondaryIcdCode(diagnosisNameToCode.get(name) || "");
                          }}
                          options={diagnosisNameOptionsWithSelected}
                          loading={loadingDiagnosis}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-600">
                          ICD-10 code
                        </label>
                        <Select
                          showSearch
                          allowClear
                          placeholder="Select"
                          value={secondaryIcdCode || undefined}
                          onChange={(val) => {
                            const code = val || "";
                            setSecondaryIcdCode(code);
                            setSecondaryDiagnosisName(diagnosisCodeToName.get(code) || "");
                          }}
                          options={diagnosisCodeOptionsWithSelected}
                          loading={loadingDiagnosis}
                          style={{ width: "100%" }}
                        />
                      </div>
                    </div>
                  </div>

                  <ReadOnlyTextArea
                    label="Symptoms"
                    value={draftVisitDetails?.symptoms || findings.join(", ")}
                    rows={2}
                    onChange={updateDraftField("symptoms")}
                  />

                  <div className="rounded-md border border-slate-200 p-3">
                    <h3 className="mb-3 text-sm font-semibold text-slate-700">
                      History of Presenting Illness
                    </h3>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <ReadOnlyField
                        label="Comments"
                        value={draftVisitDetails?.comments}
                        onChange={updateDraftField("comments")}
                      />
                      <ReadOnlyField
                        label="Frame"
                        value={draftVisitDetails?.frame}
                        onChange={updateDraftField("frame")}
                      />
                      <ReadOnlyField
                        label="Coating"
                        value={draftVisitDetails?.coating}
                        onChange={updateDraftField("coating")}
                      />
                      <ReadOnlyField
                        label="NV Assessment"
                        value={draftVisitDetails?.nvAssessment}
                        onChange={updateDraftField("nvAssessment")}
                      />
                      <ReadOnlyField
                        label="DV Assessment"
                        value={draftVisitDetails?.dvAssessment}
                        onChange={updateDraftField("dvAssessment")}
                      />
                      <ReadOnlyField
                        label="Treatment Option"
                        value={draftVisitDetails?.treatmentOption}
                        onChange={updateDraftField("treatmentOption")}
                      />
                      <ReadOnlyField
                        label="Any Surgery Recommendation?"
                        value={draftVisitDetails?.surgeryRecommendation}
                        placeholder="Select"
                        onChange={updateDraftField("surgeryRecommendation")}
                      />
                      <ReadOnlyTextArea
                        label="Onset"
                        value={draftVisitDetails?.onset}
                        rows={2}
                        onChange={updateDraftField("onset")}
                      />
                      <ReadOnlyTextArea
                        label="Character"
                        value={draftVisitDetails?.characteristics}
                        rows={2}
                        onChange={updateDraftField("characteristics")}
                      />
                      <ReadOnlyTextArea
                        label="Radiation"
                        value={draftVisitDetails?.radiation}
                        rows={2}
                        onChange={updateDraftField("radiation")}
                      />
                      <ReadOnlyTextArea
                        label="Associated Symptoms"
                        value={draftVisitDetails?.associatedSymptoms}
                        rows={2}
                        onChange={updateDraftField("associatedSymptoms")}
                      />
                      <ReadOnlyTextArea
                        label="Timing"
                        value={draftVisitDetails?.timing}
                        rows={2}
                        onChange={updateDraftField("timing")}
                      />
                      <ReadOnlyTextArea
                        label="Exacerbating Factors"
                        value={draftVisitDetails?.exacerbatingFactors}
                        rows={2}
                        onChange={updateDraftField("exacerbatingFactors")}
                      />
                      <ReadOnlyTextArea
                        label="Relieving Factors"
                        value={draftVisitDetails?.relievingFactors}
                        rows={2}
                        onChange={updateDraftField("relievingFactors")}
                      />
                      <div>
                        <label className="mb-1 block text-xs font-medium text-slate-600">
                          Severity
                        </label>
                        <Select
                          allowClear
                          placeholder="Select Severity"
                          value={severityValue}
                          onChange={(val) => {
                            setSeverityValue(val);
                            updateDraftField("severity")(val);
                          }}
                          options={Array.from({ length: 10 }, (_, i) => {
                            const v = String(i + 1);
                            return { value: v, label: v };
                          })}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <ReadOnlyTextArea
                        label="Any Treatment Taken"
                        value={draftVisitDetails?.treatmentTaken}
                        rows={2}
                        onChange={updateDraftField("treatmentTaken")}
                      />
                    </div>
                  </div>

                  <div className="rounded-md border border-slate-200 p-3">
                    <h3 className="mb-3 text-sm font-semibold text-slate-700">
                      Other Details
                    </h3>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <ReadOnlyField
                        label="Consult Reason"
                        value={draftVisitDetails?.consultReason}
                        onChange={updateDraftField("consultReason")}
                      />
                      {/* <ReadOnlyField
                        label="Complaints"
                        value={draftVisitDetails?.complaints}
                        onChange={updateDraftField("complaints")}
                      /> */}
                      <ReadOnlyField
                        label="Recommendation"
                        value={draftVisitDetails?.recommendation}
                        onChange={updateDraftField("recommendation")}
                      />
                      <div className="md:col-span-1">
                        <label className="mb-1 block text-xs font-medium text-slate-600">
                          Schedule Follow up
                        </label>
                        <DatePicker
                          value={followUpVisitDate}
                          onChange={(d) => {
                            setFollowUpVisitDate(d);
                            updateDraftField("followUpVisitDate")(
                              d ? d.format("YYYY-MM-DD") : ""
                            );
                          }}
                          style={{ width: "100%" }}
                          format="DD/MM/YYYY"
                        />
                      </div>
                      {/* <ReadOnlyField
                        label="Body Part Location"
                        value={draftVisitDetails?.bodyPartLocation}
                        onChange={updateDraftField("bodyPartLocation")}
                      />
                      <ReadOnlyField
                        label="Duration"
                        value={draftVisitDetails?.duration}
                        onChange={updateDraftField("duration")}
                      />
                      <ReadOnlyField
                        label="Adverse Effect"
                        value={draftVisitDetails?.adverseEffect}
                        onChange={updateDraftField("adverseEffect")}
                      /> */}
                      {/* <ReadOnlyField
                        label="Site"
                        value={draftVisitDetails?.site}
                        onChange={updateDraftField("site")}
                      />
                      <ReadOnlyTextArea
                        label="Clinical Notes"
                        value={draftVisitDetails?.clinicalNotes}
                        rows={2}
                        onChange={updateDraftField("clinicalNotes")}
                      /> */}
                    </div>
                  </div>
                  </div>
                </>
              ) : null}

              {activeTab === "medicine" ? (
                <>
                  <EditPrescriptionMedicineTab
                    medicineItems={medicineItems}
                    setMedicineItems={setMedicineItems}
                    pincode={
                      appointmentData?.pincode ||
                      appointmentData?.pinCode ||
                      DEFAULT_PINCODE
                    }
                    state={appointmentData?.state || DEFAULT_LOC}
                  />
                  <div className="hidden">
                  {medicineItems.length ? (
                    medicineItems.map((med, index) => (
                      <div
                        key={`${med?.id || med?.drugCode || med?.drugName || "medicine"}-${index}`}
                        className="rounded-md border border-slate-200 p-3"
                      >
                        <div className="mb-2 flex items-center justify-between">
                          <p className="font-semibold text-slate-700">
                            {med?.drugName ||
                              med?.medicineName ||
                              med?.name ||
                              "Medicine"}
                          </p>
                          <div className="flex items-center gap-2 text-slate-400">
                            <Pencil size={14} />
                            <Trash2 size={14} className="text-slate-300" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-2 text-sm text-slate-700 md:grid-cols-5">
                          <div>
                            <p className="text-sm text-slate-500">Timings</p>
                            <p>{med?.dosage || med?.frequency || "-"}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Frequency</p>
                            <p>{med?.frequency || "-"}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Instruction</p>
                            <p>{formatMedicineInstruction(med?.instruction ?? med?.afterBeforeFood) || "-"}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">No. of Days</p>
                            <p>{med?.duration || med?.noOfDays || med?.days || "-"}</p>
                          </div>
                          <div >
                            <p className="text-sm text-slate-500">Quantity</p>
                            <p>
                              {med?.quantity !== undefined && med?.quantity !== null
                                ? String(med?.quantity)
                                : med?.qty !== undefined && med?.qty !== null
                                ? String(med?.qty)
                                : "-"}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-400">No medicines available.</p>
                  )}
                  <div className="text-right text-sm font-medium text-[#4b78c2]">+Add more</div>
                  </div>
                </>
              ) : null}

              {activeTab === "tests" ? (
                <>
                  <EditPrescriptionTestsTab
                    labTestItems={labTestItems}
                    setLabTestItems={setLabTestItems}
                  />
                  <div className="hidden">
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-slate-700">Prescribed Tests:</h3>
                    {labTestItems.length ? (
                      <ul className="space-y-2">
                        {labTestItems.map((test, index) => (
                          <li
                            key={`${test?.id || test?.labTestId || test?.testName || test?.name || "test"}-${index}`}
                            className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2"
                          >
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-slate-700">
                                {test?.labTestName ||
                                  test?.testName ||
                                  test?.name ||
                                  "Lab Test"}
                              </span>
                              
                            </div>
                            <Trash2
                              size={14}
                              className="text-rose-300"
                            />
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-slate-400">No tests prescribed.</p>
                    )}
                  </div>

                  <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <h3 className="mb-2 text-sm font-semibold text-slate-700">Add Tests</h3>
                    <ReadOnlyField
                      label="Lab Test Name"
                      value={addTestName}
                      placeholder="Search for lab tests"
                    />
                    <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto]">
                      <input
                        type="text"
                        value={addTestName}
                        placeholder="Type lab test name"
                        readOnly
                        className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
                      />
                      <button
                        type="button"
                        disabled
                        className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm text-slate-400"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                  <div className="text-right text-sm font-medium text-[#4b78c2]">+Add more</div>
                  </div>
                </>
              ) : null}
            <div className="mt-5 flex items-center justify-end gap-3">
              {updateError ? (
                <p className="text-sm font-medium text-red-600">{updateError}</p>
              ) : null}
              {updateSuccess ? (
                <p className="text-sm font-medium text-emerald-600">{updateSuccess}</p>
              ) : null}
              <button
                type="button"
                onClick={handleUpdatePrescription}
                disabled={isUpdatingPrescription}
                className="rounded-md bg-[#1f4d74] px-5 py-2 text-sm font-medium text-white hover:bg-[#1a3f60] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isUpdatingPrescription ? "Updating..." : "Update Prescription"}
              </button>
            </div>
            </>
          ) : null}
        </div>
      </div>
      {ocrPreviewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#0f4168]">Prescription Scan Results</h3>
              <button
                type="button"
                onClick={() => setOcrPreviewModalOpen(false)}
                className="text-slate-500 hover:text-slate-800 text-xl font-bold leading-none"
              >
                ✕
              </button>
            </div>
            <div className="grid gap-6 grid-cols-1 xl:grid-cols-[24rem_1fr]">
              {/* Preview */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                {ocrUploadedFile?.type === "application/pdf" ? (
                  <embed src={ocrPreviewURL} type="application/pdf" width="100%" className="h-[55vh]" />
                ) : (
                  <img src={ocrPreviewURL} alt="Prescription preview" className="h-[55vh] w-full object-contain" />
                )}
              </div>
              {/* OCR Results Summary */}
              <div className="max-h-[55vh] overflow-y-auto space-y-4 pr-1">
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Medicines ({medicineItems.filter(m => String(m.id || "").startsWith("ocr-med-")).length})</h4>
                  {medicineItems.filter(m => String(m.id || "").startsWith("ocr-med-")).length > 0 ? (
                    <table className="w-full text-xs border-collapse">
                      <thead className="bg-slate-50">
                        <tr>
                          {["Name", "Dosage", "Instructions", "Days", "Qty"].map(h => (
                            <th key={h} className="px-2 py-1.5 text-left text-slate-600 font-medium">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {medicineItems.filter(m => String(m.id || "").startsWith("ocr-med-")).map((med, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-2 py-1.5">{med.drugName || "—"}</td>
                            <td className="px-2 py-1.5">{med.dosage || "—"}</td>
                            <td className="px-2 py-1.5">
                              {formatMedicineInstruction(med.instruction ?? med.afterBeforeFood) || "—"}
                            </td>
                            <td className="px-2 py-1.5">{med.noOfDays || "—"}</td>
                            <td className="px-2 py-1.5">{med.quantity || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-xs text-slate-500">No medicines detected.</p>
                  )}
                </div>
                {labTestItems.filter(t => t.source === "ocr").length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Lab Tests ({labTestItems.filter(t => t.source === "ocr").length})</h4>
                    <ul className="space-y-1">
                      {labTestItems.filter(t => t.source === "ocr").map((t, i) => (
                        <li key={i} className="text-xs text-slate-700">• {t.labTestName}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {draftVisitDetails?.recommendation && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1">Recommendation</h4>
                    <p className="text-xs text-slate-600">{draftVisitDetails.recommendation}</p>
                  </div>
                )}
                {followUpVisitDate && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1">Follow-up Date</h4>
                    <p className="text-xs text-slate-600">{dayjs(followUpVisitDate).format("D MMM YYYY")}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setOcrPreviewModalOpen(false)}
                className="rounded-md bg-[#0f4168] px-5 py-2 text-sm font-medium text-white hover:bg-[#1a3f60]"
              >
                Done — Review &amp; Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default EditPrescriptionPage;
